// BusinessFeed — a "live business feed" list for the Operational Center.
// Compact rows: icon, title, amount, time. Minimal, scannable, Stripe/Linear-inspired.

import { Box, Stack, Typography } from '@mui/material';
import { Skeleton } from '@mui/material';
import { CrudRecord } from '../../crud/types';
import { num } from '../kpis';

export type FeedEntry = {
  id: string | number;
  title: string;
  subtitle?: string;
  amount?: number;
  amountLabel?: string;
  date?: string;
  tone?: 'positive' | 'negative' | 'neutral';
};

export function BusinessFeed({
  entries,
  loading,
  emptyMessage = 'Nothing yet'
}: {
  entries: FeedEntry[];
  loading?: boolean;
  emptyMessage?: string;
}) {
  if (loading) {
    return (
      <Stack spacing={1}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={44} />
        ))}
      </Stack>
    );
  }

  if (!entries.length) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
      {entries.map((entry) => (
        <Stack key={entry.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.25, px: 0.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {entry.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[entry.subtitle, entry.date].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {entry.amount != null ? (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, flexShrink: 0, pl: 1, color: entry.tone === 'negative' ? 'error.main' : entry.tone === 'positive' ? 'success.main' : 'text.primary' }}
            >
              {entry.amountLabel ?? num(entry.amount).toLocaleString()}
            </Typography>
          ) : null}
        </Stack>
      ))}
    </Stack>
  );
}

// Helper: build feed entries from generic rows + a mapper.
export function rowsToFeed<T extends CrudRecord>(rows: T[] | undefined, map: (row: T) => FeedEntry, limit = 6): FeedEntry[] {
  return (rows ?? []).slice(0, limit).map(map);
}
